/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect } from 'react';
import useResponsiveVideo from 'effects/responsiveVideo';
import useGuid from 'effects/useGuid';
import usePrefersReducedMotion from 'effects/usePrefersReducedMotion';
import useInview from 'effects/inview';
import Play from 'components/Icons/Play/Play';
import Pause from 'components/Icons/Pause/Pause';

const DEFAULT_SRC_SET: VideoSource[] = [];

const DEFAULT_LABELS = {
  play: 'Play',
  pause: 'Pause',
};

interface VideoSource {
  orientation?: string;
  width: number;
  height?: number;
  link: string;
}

interface VideoBackgroundProps {
  className?: string;
  srcSet?: VideoSource[];
  /** The text for the play button, as the templates send it (over `labels.play`). */
  play?: string;
  labels?: { play?: string; pause?: string };
}

/**
 * This is the VideoBackground component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function VideoBackground({
  className = 'video-background',
  srcSet = DEFAULT_SRC_SET,
  play: playText,
  labels = DEFAULT_LABELS,
}: VideoBackgroundProps) {
  const guid = useGuid('video-background');
  const prefersReducedMotion = usePrefersReducedMotion();
  const [ref, inview] = useInview<HTMLVideoElement>({
    threshold: [0.1, 0.5, 0.9],
  });

  const video = useResponsiveVideo(srcSet);
  const [play, setPlay] = useState(!prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) {
      setPlay(false);
    }
  }, [prefersReducedMotion]);

  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Pause') {
        e.preventDefault();
        setPlay(play => !play);
      }
    }

    if (inview) {
      window.addEventListener('keydown', onKeydown);
    }

    if (ref.current) {
      if (inview && play) {
        ref.current.play();
      } else {
        ref.current.pause();
      }
    }

    return function cleanup() {
      window.removeEventListener('keydown', onKeydown);
    };
  }, [inview, play]);

  if (!video) return null;

  const playLabel = playText ?? labels.play;

  return (
    <div className={className}>
      <video
        id={guid ?? undefined}
        ref={ref}
        src={video.link}
        muted
        loop
        playsInline
      />
      <button
        aria-controls={guid ?? undefined}
        aria-label={play ? labels.pause : playLabel}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setPlay(play => !play);
        }}
      >
        {play ? <Pause title={labels.pause} /> : <Play title={playLabel} />}
      </button>
    </div>
  );
}

export default VideoBackground;
